import { Close } from "@mui/icons-material";
import { LoadingButton } from "@mui/lab";
import { Box, Button, Checkbox, Drawer, IconButton, Modal, Paper, TextField, Typography, useMediaQuery, useTheme } from "@mui/material";
import { Fragment, ReactNode, useState } from "react";
import { GobikesApi } from "../../config/Apis";
import { getAuthCookie } from "../auth/AuthUtil";
import { OrderModel, SubscriptionInvoiceStatus } from "../utils/Models";

interface FormState {
  loading?: boolean;
  error?: boolean;
  message?: string;
}

type Step = 'form' | 'confirmMark' | 'selectInvoices' | 'confirmMismatch';

interface UnpaidItem {
  type: 'invoice' | 'extension';
  id: number;
  invoiceNumber: number;
  cycleStart: number;
  cycleEnd: number;
  amount: number;
}

function getUnpaidItems(order: OrderModel): UnpaidItem[] {
  const laterRows = [
    ...(order.subscription_invoices || []).map(inv => ({
      type: 'invoice' as const,
      id: inv.id as number,
      cycleStart: Number(inv.cycleStart),
      cycleEnd: Number(inv.cycleEnd),
      amount: inv.amount,
      isPaid: inv.status === SubscriptionInvoiceStatus.PAID
    })),
    ...(order.order_extensions || []).filter(ext => !ext.voided).map(ext => ({
      type: 'extension' as const,
      id: ext.id as number,
      cycleStart: Number(ext.extStart),
      cycleEnd: Number(ext.extEnd),
      amount: ext.extraAmount,
      isPaid: ext.paymentDone
    }))
  ].sort((a, b) => a.cycleStart - b.cycleStart);

  return laterRows
    .map((row, i) => ({ ...row, invoiceNumber: i + 2 }))
    .filter(row => !row.isPaid)
    .map(({ isPaid, ...rest }) => rest);
}

export function RecordCashPayment(props: {
  children: ReactNode;
  order: OrderModel;
  style?: any;
  backFn: () => void;
  isSubscription?: boolean;
}) {
  const [open, setOpen]           = useState(false);
  const [amount, setAmount]       = useState<number>(0);
  const [remark, setRemark]       = useState<string>('');
  const [formState, setFormState] = useState<FormState>({});
  const [step, setStep]           = useState<Step>('form');
  const [selectedInvoiceIds, setSelectedInvoiceIds]     = useState<number[]>([]);
  const [selectedExtensionIds, setSelectedExtensionIds] = useState<number[]>([]);
  const [mismatchType, setMismatchType] = useState<'less' | 'more' | null>(null);

  const authCookie = getAuthCookie();
  const theme = useTheme();
  const isPhone = !useMediaQuery(theme.breakpoints.up("md"));

  function handleOpen() {
    setAmount(0);
    setRemark('');
    setFormState({});
    setStep('form');
    setSelectedInvoiceIds([]);
    setSelectedExtensionIds([]);
    setMismatchType(null);
    setOpen(true);
  }

  function handleClose() { setOpen(false); }

  function toggleInvoice(id: number) {
    setSelectedInvoiceIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }
  function toggleExtension(id: number) {
    setSelectedExtensionIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  }

  async function submitCashPayment(payload: { markInvoicesPaid: boolean, invoiceIds?: number[], extensionIds?: number[] }) {
    setFormState({ loading: true });
    try {
      await GobikesApi.post(
        `/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/subscription/record-cash-payment`,
        { amount, remark: remark.trim(), ...payload },
        { headers: { 'vendor-token': authCookie } }
      );
      setFormState({ error: false, message: 'Cash payment recorded!' });
      setTimeout(() => { handleClose(); props.backFn(); }, 2000);
    } catch (e: any) {
      const errorMessage = (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred';
      setFormState({ error: true, message: errorMessage, loading: false });
    }
  }

  async function handleFormSubmit(e: any) {
    e.preventDefault();

    if (!amount || amount <= 0) {
      setFormState({ error: true, message: 'Enter a valid amount' });
      return;
    }
    if (!remark || !remark.trim()) {
      setFormState({ error: true, message: 'Enter a remark' });
      return;
    }

    if (!props.isSubscription) {
      setFormState({ loading: true });
      try {
        await GobikesApi.post(
          `/vendors/hubs/${props.order.hubId}/orders/${props.order.id}/record-cash-payment`,
          { amount, remark: remark.trim() },
          { headers: { 'vendor-token': authCookie } }
        );
        setFormState({ error: false, message: 'Cash payment recorded!' });
        setTimeout(() => { handleClose(); props.backFn(); }, 2000);
      } catch (e: any) {
        const errorMessage = (e.response?.data?.message) ? 'Error: ' + e.response.data.message : 'An error occurred';
        setFormState({ error: true, message: errorMessage, loading: false });
      }
      return;
    }

    setFormState({});
    setStep('confirmMark');
  }

  function handleContinueSelection() {
    const items = getUnpaidItems(props.order);
    const selectedTotal = items
      .filter(i => (i.type === 'invoice' ? selectedInvoiceIds : selectedExtensionIds).includes(i.id))
      .reduce((sum, i) => sum + i.amount, 0);

    if (selectedTotal > amount) {
      setMismatchType('less');
      setStep('confirmMismatch');
    } else if (selectedTotal < amount) {
      setMismatchType('more');
      setStep('confirmMismatch');
    } else {
      submitCashPayment({ markInvoicesPaid: true, invoiceIds: selectedInvoiceIds, extensionIds: selectedExtensionIds });
    }
  }

  const message = formState.message && (
    <Typography sx={{ fontSize: '12px', color: formState.error ? '#c0392b' : '#27ae60', mt: 0.5, mb: 0.5 }}>
      {formState.message}
    </Typography>
  );

  function renderFormStep() {
    return (
      <Fragment>
        <Box sx={{ px: isPhone ? 0 : 2.5, pt: isPhone ? 0 : 2, pb: 1 }}>
          <TextField
            label="Amount (₹)"
            type="number"
            size="small"
            fullWidth
            value={amount || ''}
            onChange={(e) => setAmount(Number(e.target.value))}
            sx={{ mb: 1.5, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' }, '& label': { fontSize: '13px' } }}
          />
          <TextField
            label="Remark"
            size="small"
            fullWidth
            value={remark}
            onChange={(e) => setRemark(e.target.value)}
            sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '14px' }, '& label': { fontSize: '13px' } }}
          />
          {message}
        </Box>
        <Box sx={{ px: isPhone ? 0 : 2.5, pb: isPhone ? 0 : 2.5 }}>
          <form onSubmit={handleFormSubmit}>
            <LoadingButton
              variant="contained"
              type="submit"
              fullWidth
              loading={Boolean(formState.loading)}
              sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '14px', py: 1.2, background: '#8dbe2e', '&:hover': { background: '#78a225' } }}
            >
              Record
            </LoadingButton>
          </form>
        </Box>
      </Fragment>
    );
  }

  function renderConfirmMarkStep() {
    return (
      <Fragment>
        <Box sx={{ px: isPhone ? 0 : 2.5, pt: isPhone ? 0 : 2, pb: 1 }}>
          <Typography sx={{ fontSize: '14px', mb: 2 }}>
            Do you want to change outstanding amount and mark the pending invoices as paid?
          </Typography>
          {message}
        </Box>
        <Box sx={{ px: isPhone ? 0 : 2.5, pb: isPhone ? 0 : 2.5, display: 'flex', gap: 1 }}>
          <LoadingButton
            variant="outlined"
            loading={Boolean(formState.loading)}
            sx={{ flex: 1, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
            onClick={() => submitCashPayment({ markInvoicesPaid: false })}
          >
            No
          </LoadingButton>
          <Button
            variant="contained"
            sx={{ flex: 1, borderRadius: '12px', textTransform: 'none', fontWeight: 700, background: '#8dbe2e', '&:hover': { background: '#78a225' } }}
            onClick={() => setStep('selectInvoices')}
          >
            Yes
          </Button>
        </Box>
      </Fragment>
    );
  }

  function renderSelectInvoicesStep() {
    const items = getUnpaidItems(props.order);
    return (
      <Fragment>
        <Box sx={{ px: isPhone ? 0 : 2.5, pt: isPhone ? 0 : 2, pb: 1, maxHeight: '320px', overflowY: 'auto' }}>
          {items.length === 0 && (
            <Typography sx={{ fontSize: '13px', color: '#888' }}>No pending invoices for this order.</Typography>
          )}
          {items.map(item => {
            const checked = (item.type === 'invoice' ? selectedInvoiceIds : selectedExtensionIds).includes(item.id);
            const toggle = () => (item.type === 'invoice' ? toggleInvoice(item.id) : toggleExtension(item.id));
            return (
              <Box
                key={`${item.type}-${item.id}`}
                onClick={toggle}
                sx={{ display: 'flex', alignItems: 'center', gap: 1, border: checked ? '1.5px solid #8dbe2e' : '1.5px solid #e0e0e0', borderRadius: '10px', px: 1.5, py: 1, mb: 1, cursor: 'pointer', background: checked ? '#f4ffe0' : '#fafafa' }}
              >
                <Checkbox size="small" checked={checked} onChange={toggle} sx={{ p: 0, mr: 0.5 }} />
                <Box sx={{ flex: 1 }}>
                  <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>Invoice #{item.invoiceNumber}</Typography>
                </Box>
                <Typography sx={{ fontSize: '13px', fontWeight: 700 }}>₹{item.amount.toFixed(2)}</Typography>
              </Box>
            );
          })}
          {message}
        </Box>
        <Box sx={{ px: isPhone ? 0 : 2.5, pb: isPhone ? 0 : 2.5 }}>
          <LoadingButton
            variant="contained"
            fullWidth
            loading={Boolean(formState.loading)}
            sx={{ borderRadius: '12px', textTransform: 'none', fontWeight: 700, fontSize: '14px', py: 1.2, background: '#8dbe2e', '&:hover': { background: '#78a225' } }}
            onClick={handleContinueSelection}
          >
            Continue
          </LoadingButton>
        </Box>
      </Fragment>
    );
  }

  function renderConfirmMismatchStep() {
    const mismatchMessage = mismatchType === 'less'
      ? 'Cash Collected is less than the total invoice amount marked. Do you still want to proceed?'
      : 'Cash collected is more than the total invoice amount marked. Do you still want to proceed?';
    return (
      <Fragment>
        <Box sx={{ px: isPhone ? 0 : 2.5, pt: isPhone ? 0 : 2, pb: 1 }}>
          <Typography sx={{ fontSize: '14px', mb: 2 }}>{mismatchMessage}</Typography>
          {message}
        </Box>
        <Box sx={{ px: isPhone ? 0 : 2.5, pb: isPhone ? 0 : 2.5, display: 'flex', gap: 1 }}>
          <Button
            variant="outlined"
            sx={{ flex: 1, borderRadius: '12px', textTransform: 'none', fontWeight: 700 }}
            onClick={() => setStep('selectInvoices')}
          >
            No
          </Button>
          <LoadingButton
            variant="contained"
            loading={Boolean(formState.loading)}
            sx={{ flex: 1, borderRadius: '12px', textTransform: 'none', fontWeight: 700, background: '#8dbe2e', '&:hover': { background: '#78a225' } }}
            onClick={() => submitCashPayment({ markInvoicesPaid: true, invoiceIds: selectedInvoiceIds, extensionIds: selectedExtensionIds })}
          >
            Yes
          </LoadingButton>
        </Box>
      </Fragment>
    );
  }

  const stepTitle: Record<Step, string> = {
    form: 'Record Cash Payment',
    confirmMark: 'Update Outstanding?',
    selectInvoices: 'Select Invoices to Mark Paid',
    confirmMismatch: 'Confirm Amount Mismatch'
  };

  function renderStepContent() {
    switch (step) {
      case 'confirmMark': return renderConfirmMarkStep();
      case 'selectInvoices': return renderSelectInvoicesStep();
      case 'confirmMismatch': return renderConfirmMismatchStep();
      default: return renderFormStep();
    }
  }

  return (
    <Fragment>
      <span onClick={handleOpen} style={props.style}>
        {props.children}
      </span>

      {isPhone ? (
        // Phone — bottom drawer, matching the "Add Note" bottom sheet
        <Drawer
          anchor="bottom"
          open={open}
          onClose={handleClose}
          PaperProps={{ sx: { borderTopLeftRadius: 18, borderTopRightRadius: 18, padding: '20px 20px 40px' } }}
        >
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 700, fontSize: '16px' }}>{stepTitle[step]}</Typography>
          {renderStepContent()}
        </Drawer>
      ) : (
        // Desktop — centered modal
        <Modal open={open} className="gbk-modal" onClose={handleClose}>
          <Box className="gbk-modal-content" style={{ width: 'min(420px, calc(100vw - 32px))', height: 'auto', maxHeight: 'calc(100vh - 80px)', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', position: 'absolute', overflowY: 'auto' }}>
            <Paper className="gbk-paper" sx={{ borderRadius: '16px !important', overflow: 'hidden', height: 'auto !important' }}>

              {/* Header */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 2.5, py: 1.5, borderBottom: '1px solid #f0f0f0' }}>
                <Typography sx={{ fontSize: '15px', fontWeight: 700 }}>{stepTitle[step]}</Typography>
                <IconButton onClick={handleClose} size="small" sx={{ color: '#666' }}>
                  <Close fontSize="small" />
                </IconButton>
              </Box>

              {renderStepContent()}

            </Paper>
          </Box>
        </Modal>
      )}
    </Fragment>
  );
}
